import { route } from "../../urls";
import Authentication from "./authentication.mdx";
import { MDXSection } from "./snippets.tsx";

import { StatusDiagram } from "./status-diagram";

## Authentication

<Authentication token={props.token} />

## Setup

First you’ll need to ensure you have a Python environment setup:

```
python -m venv .venv
source .venv/bin/activate
```

Then install the `replicate` [Python library](https://github.com/replicate/replicate-python):

```
pip install replicate
```

In a main.py file, import `replicate`:

```python
import replicate
```

This will use the `REPLICATE_API_TOKEN` API token you’ve set up in your environment for authorization.

## Run the model

Use the `replicate.run()` method to run the model:

```python
input = {{ input }}

output = replicate.run(
    "{{ runnableName }}",
    input=input
)

{{ printOutput }}
#=> {{ output }}
```

You can learn about pricing for this model on the <a href={route("model_detail", { name: props.version._extras.model.name, username: props.version._extras.model.owner })}>model page</a>.

The `run()` function returns the output directly, which you can then use or pass as the input to another model. If you want to access the full prediction object (not just the output), use the `replicate.predictions.create()` method instead. This will return a `Prediction` object that includes the prediction id, status, logs, etc.

<MDXSection show={props.hasFileInputs}>

## File inputs

This model accepts files as input, e.g. {props.fileInputField}. You can provide a file as input using a URL, a local file on your computer, or a base64 encoded object:

### Option 1: Hosted file

Use a URL as in the earlier example:

```python
{{ fileInputField }} = "{{ fileInputValue }}";
```

This is useful if you already have a file hosted somewhere on the internet.

### Option 2: Local file

You can provide Replicate with a file object and the library will handle the upload for you:

```python
{{ fileInputField }} = open("./path/to/my/{{ fileLocalValue }}", "rb");
```

### Option 3: Data URI

Lastly, you can create a data URI consisting of the base64 encoded data for your file, but this is only recommended if the file is < 1mb:

```python
import base64

with open("./path/to/my/{{ fileLocalValue }}", 'rb') as file:
  data = base64.b64encode(file.read()).decode('utf-8')
  {{ fileInputField }} = f"data:application/octet-stream;base64,{data}"
```

Then pass the file as part of the input:

```python
input = {{ inputWithFile }}

output = replicate.run(
    "{{ runnableName }}",
    input=input
)

{{ printOutput }}
#=> {{ output }}
```

</MDXSection>

<MDXSection show={props.streaming !== "none"}>

## Streaming

This model supports streaming. This allows you to receive output as the model is running:

```python
input = {{ input }}

for event in replicate.stream(
    "{{ runnableName }}",
    input=input
):
    {{ printStreamEvent }}
    #=> {{ outputStreamEvent }}
```

## Streaming in the browser

The Python library is intended to be run on the server. However once the prediction has been created its output can be streamed directly from the browser.

The streaming URL uses a standard format called [Server Sent Events](https://developer.mozilla.org/en-US/docs/Web/API/Server-sent_events/Using_server-sent_events) (or `text/event-stream`) built into all web browsers.

A common implementation is to use a web server to create the prediction using `replicate.predictions.create` , passing the `stream` property set to `true` . Then the [`urls.stream`](http://urls.stream) property of the response contains a url that can be returned to your frontend application:

```python
input = {{ input }}

# POST /run_prediction
def handler(request):
    prediction = replicate.predictions.create(
        {{ runnableFieldName }}="{{ runnableFieldValue }}",
        input=input,
        stream=True,
    )

    return Response.json({
        'url': prediction.urls['stream']
    })
    # Returns {"url": "https://replicate-stream..."}
}
```

Make a request to the server to create the prediction and use the built-in `EventSource` object to read the returned `url`.

```jsx
const response = await fetch("/run_prediction", { method: "POST" });
const { url } = await response.json();

const source = new EventSource(url);
source.addEventListener("output", (evt) => {
  console.log(evt.data) //=> {{ outputStreamEvent }}
});
source.addEventListener("done", (evt) => {
  console.log("stream is complete");
});
```

</MDXSection>

## Prediction lifecycle

Running predictions and trainings can often take significant time to complete, beyond what is reasonable for an HTTP request/response.

When you run a model on Replicate, the prediction is created with a `“starting”` state, then instantly returned. This will then move to `"processing"` and eventual one of `“successful”`, `"failed"` or `"canceled"`.

<StatusDiagram />

You can explore the prediction lifecycle by using the `prediction.reload()` method update the prediction to it's latest state.

<details>

<summary>Show example</summary>

<div className="details-inner">

```python
import time

prediction = replicate.predictions.create(
  {{ runnableFieldName }}="{{ runnableFieldValue }}",
  input=input
)
#=> Prediction(id='z3wbih3bs64of7lmykbk7tsdf4', status="starting", ...)

prediction.reload()
#=> Prediction(id='z3wbih3bs64of7lmykbk7tsdf4', status="processing", ...)


for i in range(5):
  prediction.reload()
  if prediction.status in {"succeeded", "failed", "canceled"}:
    break

  # Wait for 2 seconds and then try again.
  time.sleep(2)

print(prediction);
#=> Prediction(id='z3wbih3bs64of7lmykbk7tsdf4', status="successful", ...)
```

</div>

</details>

## Webhooks

Webhooks provide real-time updates about your prediction. Specify an endpoint when you [create a prediction](https://replicate.com/docs/reference/http#predictions.create), and Replicate will send HTTP POST requests to that URL when the prediction is created, updated, and finished.

It is possible to provide a URL to the `predictions.create()` function that will be requested by Replicate when the prediction status changes. This is an alternative to polling.

To receive webhooks you’ll need a web server. The following example uses [AIOHTTP](https://docs.aiohttp.org/en/stable/index.html), a basic webserver built on top of Python’s asyncio library, but this pattern will apply to most frameworks.

<details>

<summary>Show example</summary>

<div className="details-inner">

```python
from aiohttp import web

# NOTE: This should point to the internet facing endpoint for your application.
callback_url = "https://my.app/webhooks/replicate"

# Create a python webserver using aiohttp to handle the webhook and push
# the completed prediction into our queue.
routes = web.RouteTableDef()

# Add a request handler at /webhooks/replicate to receive the request.
@routes.post('/webhooks/replicate')
async def callback_replicate(request):
  prediction = await request.json()
  print(prediction)
  #=> Prediction(id='z3wbih3bs64of7lmykbk7tsdf4', ...)
  return web.Response(text="OK")

# Start the webserver.
app = web.Application()
app.add_routes(routes)

web.run_app(app)
```
</div>

</details>

Then create the prediction passing in the webhook URL and specify which events you want to receive out of `"start"` , `"output"` `”logs”` and `"completed"`.

```python
input = {{ input }}

callback_url = "https://my.app/webhooks/replicate"
replicate.predictions.create(
  {{ runnableFieldName }}="{{ runnableFieldValue }}",
  input=input,
  webhook=callback_url,
  webhook_events_filter=["completed"]
)

# The server will now handle the event and log:
#=> Prediction(id='z3wbih3bs64of7lmykbk7tsdf4', ...)
```

<aside>
The `replicate.run()` method is not used here. Because we're using webhooks, and we don’t need to poll for updates.
</aside>

From a security perspective it is also possible to verify that the webhook came from Replicate, check out our documentation on [verifying webhooks](https://replicate.com/docs/webhooks#verifying-webhooks) for more information.

## Access a prediction

You may wish to access the prediction object. In these cases it’s easier to use the `replicate.predictions.create()` function, which return the prediction object.

Though note that these functions will only return the created prediction, and it will not wait for that prediction to be completed before returning. Use `replicate.predictions.get()` to fetch the latest prediction.

```python
import replicate

input = {{ input }}

prediction = replicate.predictions.create(
  {{ runnableFieldName }}="{{ runnableFieldValue }}",
  input=input
)
#=> Prediction(id='z3wbih3bs64of7lmykbk7tsdf4', ...)
```


## Cancel a prediction

You may need to cancel a prediction. Perhaps the user has navigated away from the browser or canceled your application. To prevent unnecessary work and reduce runtime costs you can use `prediction.cancel()` method to call the `predictions.cancel` endpoint.

```python
input = {{ input }}

prediction = replicate.predictions.create(
  {{ runnableFieldName }}="{{ runnableFieldValue }}",
  input=input
)

prediction.cancel()
```

## Async Python methods

[asyncio](https://docs.python.org/3/library/asyncio.html) is a module built into Python's standard library for writing concurrent code using the async/await syntax.

Replicate's Python client has support for `asyncio`. Each of the methods has an async equivalent prefixed with `async_<name>`.

```python
input = {{ input }}

prediction = replicate.predictions.create(
  {{ runnableFieldName }}="{{ runnableFieldValue }}",
  input=input
)

prediction = await replicate.predictions.async_create(
  {{ runnableFieldName }}="{{ runnableFieldValue }}",
  input=input
)
```
